import { IconAlertTriangle, IconCheck, IconChecks, TablerIconsProps } from '@tabler/icons-react';
import { AppointmentMeta } from 'hooks/appointment/useAppointmentMeta';

import { AthleteAppointmentState } from './useAthleteAppointmentState';

export interface AthleteAppointmentMeta {
  cancelAction: 'cancelReservation' | 'lateCancelReservation' | null;
  color: string;
  backgroundColor: string;
  Icon?: (props: TablerIconsProps) => JSX.Element;
  stateText: string | null;
  supplementalText: string | null;
}

export default function useAthleteAppointmentMeta(
  athleteAppointmentState: AthleteAppointmentState,
  appointmentMeta: AppointmentMeta,
): AthleteAppointmentMeta {
  if (athleteAppointmentState === 'noRelation') {
    const athleteAppointmentMeta: AthleteAppointmentMeta = {
      cancelAction: null,
      color: 'dimmed',
      backgroundColor: 'white',
      stateText: null,
      supplementalText: null,
    };

    return athleteAppointmentMeta;
  }

  if (athleteAppointmentState === 'reserved') {
    const athleteAppointmentMeta: AthleteAppointmentMeta = {
      cancelAction: null,
      color: 'green.7',
      backgroundColor: 'green.0',
      Icon: IconCheck,
      stateText: 'Reserved',
      supplementalText: null,
    };

    if (appointmentMeta.isWithinCancellationWindow) {
      athleteAppointmentMeta.cancelAction = 'cancelReservation';
    } else if (appointmentMeta.isWithinLateCancellationWindow) {
      athleteAppointmentMeta.cancelAction = 'lateCancelReservation';
    }
    return athleteAppointmentMeta;
  }

  if (athleteAppointmentState === 'reservedUncredited') {
    const athleteAppointmentMeta: AthleteAppointmentMeta = {
      cancelAction: null,
      color: 'kiloBlue.7',
      backgroundColor: 'white',
      Icon: IconCheck,
      stateText: 'Reserved',
      supplementalText: '(Uncredited)',
    };
    if (appointmentMeta.isWithinCancellationWindow) {
      athleteAppointmentMeta.cancelAction = 'cancelReservation';
    }
    return athleteAppointmentMeta;
  }

  if (
    athleteAppointmentState === 'reservedAndCheckedIn' ||
    athleteAppointmentState === 'notReservedButCheckedIn'
  ) {
    const athleteAppointmentMeta: AthleteAppointmentMeta = {
      cancelAction: null,
      color: 'green.7',
      backgroundColor: 'green.1',
      Icon: IconChecks,
      stateText: 'Checked In',
      supplementalText: null,
    };
    return athleteAppointmentMeta;
  }

  if (athleteAppointmentState === 'lateCancelWithCreditDeduction') {
    const athleteAppointmentMeta: AthleteAppointmentMeta = {
      cancelAction: null,
      color: 'red.7',
      backgroundColor: 'white',
      Icon: IconAlertTriangle,
      stateText: 'Late Cancel',
      supplementalText: '(Credit deducted)',
    };
    return athleteAppointmentMeta;
  }

  if (athleteAppointmentState === 'lateCancelWithoutCreditDeduction') {
    const athleteAppointmentMeta: AthleteAppointmentMeta = {
      cancelAction: null,
      color: 'red.7',
      backgroundColor: 'white',
      Icon: IconAlertTriangle,
      stateText: 'Late Cancel',
      supplementalText: null,
    };
    return athleteAppointmentMeta;
  }

  if (athleteAppointmentState === 'noShowWithCreditDeduction') {
    const athleteAppointmentMeta: AthleteAppointmentMeta = {
      cancelAction: null,
      color: 'red.7',
      backgroundColor: 'white',
      Icon: IconAlertTriangle,
      stateText: 'No Show',
      supplementalText: '(Credit deducted)',
    };
    return athleteAppointmentMeta;
  }

  if (athleteAppointmentState === 'noShowWithoutCreditDeduction') {
    const athleteAppointmentMeta: AthleteAppointmentMeta = {
      cancelAction: null,
      color: 'red.7',
      backgroundColor: 'white',
      Icon: IconAlertTriangle,
      stateText: 'No Show',
      supplementalText: null,
    };
    return athleteAppointmentMeta;
  }

  return {
    cancelAction: null,
    color: 'dimmed',
    backgroundColor: 'white',
    stateText: null,
    supplementalText: null,
  };
}
